import { defineCollection } from "@rebasepro/cms-types";
import gamesCollection from "./games";
import songsCollection from "./songs";
import type { CollectionConfig } from "@rebasepro/types";
import { SAY, moment } from "../words";

// Every round of every party game: the song, and how many named it. backend/src/games.ts fills it from the event log
// (songbuzz_events: song), each round tied to its game by room and time.
const gameSongsCollection = defineCollection({
    name: "Songs played",
    singularName: "Song played",
    slug: "game_songs",
    table: "songbuzz_game_songs",
    description: "Every round of every party game: which song, and how many players named it.",
    securityRules: [{ operation: "select", roles: ["admin"] }],
    properties: {
        id: { name: "Round", type: "number", isId: "manual", columnType: "bigint", admin: { readOnly: true } },
        game: {
            name: "Game", type: "relation",
            relation: { kind: "belongsTo", target: () => gamesCollection, relationName: "game", localKey: "game_id" },
        },
        at: { name: "Played", type: "date", validation: { required: true }, admin: { readOnly: true } },
        title: { name: "Title", type: "string", admin: { readOnly: true } },
        artist: { name: "Artist", type: "string", admin: { readOnly: true } },
        year: { name: "Year", type: "number", columnType: "integer", admin: { readOnly: true } },
        deezerId: { name: "Deezer id", type: "number", columnType: "bigint", admin: { readOnly: true, hideFromCollection: true } },
        // the song in the catalogue: its plays everywhere, its lyrics, a ban
        song: {
            name: "In the catalogue", type: "relation",
            relation: { kind: "via", cardinality: "one", target: (): CollectionConfig => songsCollection, relationName: "song", joinPath: [{ table: "songbuzz_songs", on: { from: "deezer_id", to: "id" } }] },
            admin: { readOnly: true },
        },
        roundType: { name: "Round", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "round" } } },
        players: { name: "Players in the round", type: "number", columnType: "integer", admin: { readOnly: true } },
        answered: { name: "Answered", description: "Players who gave any answer", type: "number", columnType: "integer", admin: { readOnly: true } },
        named: { name: "Got it right", type: "number", columnType: "integer", admin: { readOnly: true } },
        skipped: { name: "Skipped", type: "boolean", admin: { readOnly: true } },
        source: { name: "Dealt from", description: "The music it came from", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "mix" } } },
    },
    admin: {
        icon: "ListMusic",
        group: "Songs",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: "title",
            // who sang it, then the round: when, which kind, how many got it
            subtitle: ({ entity }) => {
                const v = entity.values;
                const got = v.skipped ? "skipped" : `${v.named ?? 0} of ${v.players ?? "?"} got it`;
                return [[v.artist, v.year].filter(Boolean).join(", "), moment(v.at), v.roundType && SAY.round(v.roundType), got].filter(Boolean).join(" · ");
            },
            date: "at",
        },
        listProperties: ["named", "players", "source"],
        form: {
            sections: [
                { key: "round", properties: ["game", "song", "title", "artist", "year", "roundType", "source", "players", "answered", "named", "skipped", "at"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["id", "deezerId"] },
            ],
        },
    },
});

export default gameSongsCollection;
